import api from './api';
import Logger from '../utils/logger';

export const getTransactions = async (params) => {
  try {
    // Log the incoming params
    Logger.debug('getTransactions params:', {
      accountIds: params.accountIds,
      institutionIds: params.institutionIds
    });

    // Clean up undefined and empty values
    const cleanParams = { ...params };
    Object.keys(cleanParams).forEach(key => {
      if (cleanParams[key] === undefined || cleanParams[key] === '' || 
          (Array.isArray(cleanParams[key]) && cleanParams[key].length === 0)) {
        delete cleanParams[key];
      }
    });

    // Convert arrays to comma-separated strings
    if (Array.isArray(cleanParams.accountIds)) {
      cleanParams.accountIds = cleanParams.accountIds.join(',');
    }
    if (Array.isArray(cleanParams.institutionIds)) {
      cleanParams.institutionIds = cleanParams.institutionIds.join(',');
    }

    // Log the cleaned params
    Logger.debug('Cleaned params:', {
      accountIds: cleanParams.accountIds,
      institutionIds: cleanParams.institutionIds
    });

    const response = await api.get('/transactions', { params: cleanParams });
    return response.data;
  } catch (error) {
    Logger.error('Failed to fetch transactions', error);
    throw error;
  }
};

export const getTransactionsByAccount = async (accountId) => {
  try {
    const response = await api.get(`/transactions/${accountId}`);
    return response.data;
  } catch (error) {
    Logger.error('Failed to fetch account transactions', error);
    throw error;
  }
};

export const syncTransactions = async (accountId, fullSync = false) => {
  try {
    const response = await api.post(`/transactions/${accountId}/sync${fullSync ? '?fullSync=true' : ''}`);
    return response.data;
  } catch (error) {
    Logger.error('Transaction sync failed', error);
    throw error;
  }
};

export const startFullHistoricalSync = async () => {
  try {
    const response = await api.post('/transactions/sync/historical');
    return response.data;
  } catch (error) {
    Logger.error('Historical sync failed to start', error);
    throw error;
  }
};

export const assignBudgetCategory = async (transactionId, budgetId, categoryId) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/category`, {
      budgetId,
      categoryId
    });
    return response;
  } catch (error) {
    Logger.error('Failed to assign budget category', error);
    throw error;
  }
};

/**
 * Move a transaction to a different budget period.
 * @param {string} transactionId
 * @param {string|null} budgetId - Target budget id, or null to revert to the actual date's budget
 */
export const setBudget = async (transactionId, budgetId) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/budget`, {
      budgetId
    });
    return response;
  } catch (error) {
    Logger.error('Failed to set transaction budget', error);
    throw error;
  }
};

/**
 * Mark (or unmark) a transaction as reimbursable.
 * @param {string} transactionId
 * @param {number} reimbursableCents - Amount owed back in cents; 0 to unmark
 */
export const markReimbursable = async (transactionId, reimbursableCents) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/mark-reimbursable`, {
      reimbursableCents
    });
    return response;
  } catch (error) {
    Logger.error('Failed to update reimbursable status', error);
    throw error;
  }
};

export const categorizeSimilarTransactions = async (transactionId, budgetId, categoryId) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/categorize-similar`, {
      budgetId,
      categoryId
    });
    return response.data;
  } catch (error) {
    Logger.error('Failed to categorize similar transactions', error);
    throw error;
  }
};

export const getConnectedAccounts = async () => {
  try {
    const response = await api.get('/accounts');
    // Unified endpoint returns { accounts: [...] } with flat array
    return response.data?.accounts || [];
  } catch (error) {
    // Return empty array for family permission denied (children can't view accounts)
    if (error.response?.status === 403) {
      return [];
    }
    Logger.error('Failed to fetch connected accounts', error);
    throw error;
  }
};

/**
 * Refresh balance for a single account (real-time from Plaid)
 * @param {string} accountId - The account ID to refresh
 * @returns {Promise<{accountId: string, balance: {available: number, current: number, limit: number, isoCurrencyCode: string, lastRefreshed: string}}>}
 */
export const refreshAccountBalance = async (accountId) => {
  try {
    const response = await api.post(`/accounts/${accountId}/balance/refresh`);
    return response.data;
  } catch (error) {
    Logger.error('Failed to refresh account balance', error);
    throw error;
  }
};

/**
 * Refresh balances for all accounts (real-time from Plaid)
 * @returns {Promise<{message: string, accounts: Array, errors?: Array}>}
 */
export const refreshAllBalances = async () => {
  try {
    const response = await api.post('/accounts/balance/refresh');
    return response.data;
  } catch (error) {
    Logger.error('Failed to refresh all account balances', error);
    throw error;
  }
}; 